import { type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { type AuditSourceView, type AuditTrailEntryView } from '@vello/api-client';
import { Badge, Button, EmptyState, Field, ListTable, Notice, PageHeader, Panel, Select, TextInput, type Column, Text, Row, AUTO_REFRESH_MS, useAutoRefresh } from '@vello/web-theme';
import {
  MARKET_AUDIT_SOURCES,
  PLATFORM_AUDIT_SOURCES,
  customPeriod,
  detailLine,
  formatMarketInstant,
  marketDate,
  marketTimeZone,
  ridesReportsAuthority,
  scopeTimeZone,
} from '../../src/index';
import { ScopeMarketNotice, useScopeMarket } from '../components/scope-market';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { useAuditTrailText } from '../i18n/audit-trail-text';
import { PersonName } from '../components/person-name';
const DAY_MS = 86_400_000;

export function AuditTrailScreen(): ReactElement {
  const { runtime, scope, markets, grants } = useAdmin();
  const { t, tp, lang } = useT();
  const { v, fail, refusal: refusalOf } = useVocab();
  const auditText = useAuditTrailText();
  const authority = ridesReportsAuthority(grants);

  // The trail follows the header scope (owner, 2026-10-12): a global scope reads the platform trail; a country or city scope
  // reads the scoped market's. No screen-level scope picker — switch the scope at the top to read another trail.
  const { market: scopeMarket, choices } = useScopeMarket();
  const isPlatform = scope.current.country === undefined && scope.current.city === undefined;
  const market = isPlatform ? undefined : scopeMarket;
  const ready = isPlatform || market !== undefined;
  const zone = isPlatform ? scopeTimeZone(scope.current, markets) : marketTimeZone(market, markets);
  const sources: readonly AuditSourceView[] = isPlatform ? PLATFORM_AUDIT_SOURCES : MARKET_AUDIT_SOURCES;

  const [fromDay, setFromDay] = useState(() => marketDate(new Date(Date.now() - 6 * DAY_MS), zone));
  const [toDay, setToDay] = useState(() => marketDate(new Date(), zone));
  const [source, setSource] = useState('');
  const [actor, setActor] = useState('');
  const [action, setAction] = useState('');
  const [entries, setEntries] = useState<readonly AuditTrailEntryView[] | undefined>(undefined);
  const [next, setNext] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const readSeq = useRef(0);
  // Once the operator loaded older pages the first page is not re-read in the background (it would drop them).
  const [paged, setPaged] = useState(false);

  const refuse = useCallback(
    (reasonCode: string | undefined, fallback: string, status?: number): string => {
      const text = refusalOf('refusal.admin.rides_reports', { code: fallback, reasonCode, status });
      return text ?? t('rrep.err.generic', { code: fail({ code: fallback, reasonCode }) });
    },
    [t, fail, refusalOf],
  );

  const period = customPeriod(fromDay, toDay, zone);
  const chosenSource = sources.includes(source as AuditSourceView) ? (source as AuditSourceView) : undefined;

  const load = useCallback(
    async (cursor?: string): Promise<void> => {
      const seq = ++readSeq.current;
      if (!ready || period === undefined) {
        setEntries(undefined);
        setNext(undefined);
        setError(period === undefined && ready ? t('rrep.err.period') : undefined);
        return;
      }
      const res = await runtime.ops.auditTrail({
        ...(market !== undefined ? { market } : {}),
        from: period.from,
        to: period.to,
        ...(chosenSource !== undefined ? { source: [chosenSource] } : {}),
        ...(actor.trim() !== '' ? { actor: actor.trim() } : {}),
        ...(action.trim() !== '' ? { action: action.trim() } : {}),
        ...(cursor !== undefined ? { cursor } : {}),
      });
      if (seq !== readSeq.current) return;
      if (res.ok) {
        setPaged(cursor !== undefined);
        setEntries((prev) => (cursor !== undefined ? [...(prev ?? []), ...res.value.entries] : res.value.entries));
        setNext(res.value.nextCursor);
        setError(undefined);
      } else {
        setEntries((prev) => prev ?? []);
        setError(refuse(res.error.reasonCode, res.error.code, res.error.status));
      }
    },
    // The period is derived from the typed days and the zone; depend on those, not on the object identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [runtime, ready, market, period?.from, period?.to, chosenSource, actor, action, refuse, t],
  );

  useEffect(() => {
    setEntries(undefined);
    void load();
  }, [load]);
  useAutoRefresh(() => load(), { intervalMs: AUTO_REFRESH_MS.report, enabled: ready && period !== undefined && !paged });

  const columns: readonly Column<AuditTrailEntryView>[] = [
    { key: 'at', header: t('rrep.audit.col.at'), width: 'minmax(160px, 1fr)', render: (e) => <Text role="caption" tone="secondary" mono>{formatMarketInstant(e.at, zone, lang, 'datetime-seconds')}</Text> },
    { key: 'source', header: t('rrep.audit.col.source'), width: 'minmax(150px, 1fr)', render: (e) => <Badge tone="neutral">{v('reports.audit_source', e.source)}</Badge> },
    { key: 'actor', header: t('rrep.audit.col.actor'), width: 'minmax(110px, 0.8fr)', render: (e) => <Text role="caption" tone="secondary" mono>{e.actor.length > 14 ? <PersonName id={e.actor} /> : e.actor}</Text> },
    { key: 'action', header: t('rrep.audit.col.action'), width: 'minmax(170px, 1.2fr)', render: (e) => <strong>{auditText.action(e)}</strong> },
    { key: 'target', header: t('rrep.audit.col.target'), width: 'minmax(150px, 1fr)', render: (e) => <Text role="caption" tone="secondary">{auditText.target(e)}</Text> },
    { key: 'reason', header: t('rrep.audit.col.reason'), width: 'minmax(140px, 1fr)', render: (e) => <Text role="caption" tone="secondary">{auditText.reason(e)}</Text> },
    { key: 'detail', header: t('rrep.audit.col.detail'), width: 'minmax(220px, 1.6fr)', render: (e) => <Text role="caption" tone="secondary" title={detailLine(e.detail)}>{e.detail === undefined ? '—' : tp('rrep.audit.detail', Object.keys(e.detail).length)}</Text> },
  ];

  return (
    <>
      <PageHeader title={t('rrep.audit.title')} subtitle={t('rrep.audit.subtitle')} />

      {!authority.mayAudit && (
        <div>
          <Notice tone="neutral">{t('rrep.audit.noGrant')}</Notice>
        </div>
      )}

      <Panel>
        <Row gap={4} align="stretch" wrap>
          <Field label={t('rrep.filter.from')} htmlFor="rrep-audit-from">
            <TextInput id="rrep-audit-from" type="date" value={fromDay} onChange={(e) => setFromDay(e.target.value)} />
          </Field>
          <Field label={t('rrep.filter.to')} htmlFor="rrep-audit-to">
            <TextInput id="rrep-audit-to" type="date" value={toDay} onChange={(e) => setToDay(e.target.value)} />
          </Field>
          <div className="vello-admin-a-field">
            <Field label={t('rrep.audit.filter.source')} htmlFor="rrep-audit-source">
              <Select id="rrep-audit-source" value={chosenSource ?? ''} onChange={(e) => setSource(e.target.value)}>
                <option value="">{t('rrep.audit.filter.allSources')}</option>
                {sources.map((s) => (
                  <option key={s} value={s}>{v('reports.audit_source', s)}</option>
                ))}
              </Select>
            </Field>
          </div>
          <Field label={t('rrep.audit.filter.actor')} htmlFor="rrep-audit-actor">
            <TextInput id="rrep-audit-actor" value={actor} maxLength={64} onChange={(e) => setActor(e.target.value)} />
          </Field>
          <Field label={t('rrep.audit.filter.action')} htmlFor="rrep-audit-action" hint={t('rrep.audit.filter.actionHint')}>
            <TextInput id="rrep-audit-action" value={action} maxLength={64} onChange={(e) => setAction(e.target.value)} />
          </Field>
        </Row>
      </Panel>

      {error !== undefined && (
        <div>
          <Notice tone="danger">{error}</Notice>
        </div>
      )}

      {!ready ? (
        <Panel>
          <ScopeMarketNotice choices={choices} />
        </Panel>
      ) : (
        <>
          <ListTable
            columns={columns}
            rows={entries ?? []}
            rowKey={(e) => `${e.source}:${e.id}`}
            loading={entries === undefined && error === undefined}
            loadingLabel={t('ui.loading')}
            error={error !== undefined && (entries === undefined || entries.length === 0) ? t('ui.loadFailed') : undefined}
            onRetry={() => void load()}
            retryLabel={t('ui.retry')}
            empty={<EmptyState title={t('rrep.audit.empty.title')} detail={t('rrep.audit.empty.detail')} />}
            minWidth={1100}
          />
          {next !== undefined && (
            <div>
              <Button variant="secondary" onClick={() => void load(next)}>{t('rrep.grid.more')}</Button>
            </div>
          )}
        </>
      )}
    </>
  );
}
